import { buildDefaultSongBackgroundUrl, buildGenreBackgroundUrl, buildOccasionBackgroundUrl } from "./general-config";

export const MUSIC_DISPLAY_PREFIX = "MusicGeneration";

/** Short label shown in the player header (full filename on hover). */
export const MUSIC_UI_SHORT_LABEL = "MusicGenerator";

function getExtension(filename?: string | null): string {
  if (!filename) return "wav";
  const dot = filename.lastIndexOf(".");
  if (dot < 0) return "wav";
  return filename.slice(dot + 1).toLowerCase() || "wav";
}

export type GeneratedAudioFormat = "mp3" | "wav";

/** Reads `.mp3` / `.wav` from a blob URL or storage path. */
export function resolveAudioExtensionFromPath(
  path?: string | null,
): GeneratedAudioFormat | null {
  if (!path) return null;
  const match = path.split("?")[0].match(/\.(mp3|wav)$/i);
  if (!match?.[1]) return null;
  return match[1].toLowerCase() as GeneratedAudioFormat;
}

/** Actual generated format — prefers blob storage path over display filename. */
export function resolveGeneratedAudioFormat(music?: {
  filename?: string | null;
  blobPath?: string | null;
} | null): GeneratedAudioFormat {
  return (
    resolveAudioExtensionFromPath(music?.blobPath) ??
    resolveAudioExtensionFromPath(music?.filename) ??
    "wav"
  );
}

export function audioMimeTypeForFormat(format: GeneratedAudioFormat): string {
  return format === "mp3" ? "audio/mpeg" : "audio/wav";
}

/** e.g. MusicGeneration_1710000000000.wav */
/** e.g. MusicGeneration_1710000000000.wav */
export function buildMusicGenerationFilename(
  extension = "wav",
  timestampMs = Date.now(),
): string {
  const safeExt = extension.replace(/^\./, "").toLowerCase() || "wav";
  return `${MUSIC_DISPLAY_PREFIX}_${timestampMs}.${safeExt}`;
}

/** Alias used when passing output filename to generation APIs (no extension). */
export function buildMusicGenerationOutputBaseName(
  timestampMs = Date.now(),
): string {
  return `${MUSIC_DISPLAY_PREFIX}_${timestampMs}`;
}

export function isMusicGenerationDisplayFilename(
  filename?: string | null,
): boolean {
  if (!filename) return false;
  const base = filename.replace(/\.[^/.]+$/, "");
  return base.startsWith(`${MUSIC_DISPLAY_PREFIX}_`);
}

/** Blob storage names are compact job ids, e.g. fb1539f55bab48fc801bbec0b5af5c05.wav */
export function isBlobStorageFileName(filename?: string | null): boolean {
  if (!filename) return false;
  const base = filename.replace(/\.[^/.]+$/, "");
  return /^[a-f0-9]{32}$/i.test(base);
}

/** Short UI label shown in the player (hover shows full filename). */
export function getMusicDisplayShortName(
  musicId?: string | null,
  filename?: string | null,
): string | undefined {
  const trimmed = filename?.trim();
  if (
    trimmed &&
    !isBlobStorageFileName(trimmed) &&
    !isMusicGenerationDisplayFilename(trimmed)
  ) {
    return trimmed.replace(/\.[^/.]+$/, "");
  }
  if (
    musicId?.trim() ||
    isMusicGenerationDisplayFilename(trimmed)
  ) {
    return MUSIC_UI_SHORT_LABEL;
  }
  if (trimmed) {
    return trimmed.replace(/\.[^/.]+$/, "");
  }
  return undefined;
}

/** Full filename for tooltips / hover — uses resolved display filename when available. */
export function getMusicDisplayFullName(
  musicId?: string | null,
  filename?: string | null,
): string | undefined {
  const trimmed = filename?.trim();
  if (trimmed && !isBlobStorageFileName(trimmed)) {
    return trimmed;
  }
  if (trimmed && isMusicGenerationDisplayFilename(trimmed)) {
    return trimmed;
  }
  if (musicId?.trim()) {
    return buildMusicGenerationFilename(getExtension(filename));
  }
  return trimmed ?? undefined;
}

export interface SongDisplayDetails {
  recipientName: string;
  occasion: string;
  genre: string;
  songTitle: string;
  songDescription: string;
  fileExt: string;
}

export function extractSongDisplayDetails(
  musicResult?: {
    filename?: string | null;
    customData?: Record<string, unknown> | null;
    editorRestore?: {
      prompt?: string;
      selections?: Record<string, string[]>;
    } | null;
  } | null
): SongDisplayDetails {
  if (!musicResult) {
    return {
      recipientName: "your loved one",
      occasion: "special occasion",
      genre: "custom",
      songTitle: "Your Custom Song",
      songDescription: "A joyful tribute to the one who stood by you",
      fileExt: "MP3",
    };
  }

  const customData = (musicResult.customData || {}) as Record<string, any>;

  const recipientName =
    (typeof customData.name === "string" && customData.name.trim()) ||
    (typeof customData.songfor === "string" && customData.songfor.trim()) ||
    "your loved one";

  const occasion =
    (typeof customData.occasion === "string" && customData.occasion.trim()) ||
    "special occasion";

  const genre =
    (typeof customData.genre === "string" && customData.genre.trim()) ||
    "custom";

  const songTitle =
    (musicResult.filename && !musicResult.filename.startsWith(MUSIC_DISPLAY_PREFIX) && !isBlobStorageFileName(musicResult.filename)
      ? musicResult.filename.replace(/\.[^/.]+$/, "")
      : recipientName && recipientName !== "your loved one"
        ? `Song for ${recipientName}`
        : "Your Custom Song");

  const fileExt = resolveGeneratedAudioFormat(musicResult).toUpperCase();

  const displayOccasionText = occasion && occasion !== "special occasion" ? `${occasion.toLowerCase()} ` : "";
  const songDescription = `A joyful ${displayOccasionText}tribute to the one who stood by you`.replace(/\s+/g, " ");

  return {
    recipientName,
    occasion,
    genre,
    songTitle,
    songDescription,
    fileExt,
  };
}

export const PREDEFINED_OCCASION_SLUGS = new Set([
  "just-because",
  "i-love-you",
  "birthday",
  "anniversary",
  "missing-you",
  "thank-you",
  "apology",
  "wedding",
  "friendship",
  "in-memory",
  "milestone",
  "proposal",
  "retirement",
  "graduation",
]);

export const PREDEFINED_GENRE_SLUGS = new Set([
  "hip-hop",
  "electronic",
  "pop",
  "rnb",
  "rock",
  "jazz",
  "lo-fi",
  "country",
  "acoustic",
  "classical",
  "afrobeats",
  "ambient",
]);

export function toImageSlug(text?: string): string {
  if (!text) return "";
  return text
    .toLowerCase()
    .trim()
    .replace(/&/g, "n")
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

export function getOccasionBackgroundImage(occasion?: string): string | undefined {
  if (!occasion || typeof occasion !== "string" || !occasion.trim()) return undefined;
  const slug = toImageSlug(occasion);
  if (PREDEFINED_OCCASION_SLUGS.has(slug)) {
    return buildOccasionBackgroundUrl(slug);
  }
  return undefined;
}

export function getGenreBackgroundImage(genre?: string): string | undefined {
  if (!genre || typeof genre !== "string" || !genre.trim()) return undefined;
  const slug = toImageSlug(genre);
  if (PREDEFINED_GENRE_SLUGS.has(slug)) {
    return buildGenreBackgroundUrl(slug);
  }
  return undefined;
}

export function getSongCardBackgroundImage(options?: {
  occasion?: string;
  genre?: string;
}): string {
  const occasionImg = getOccasionBackgroundImage(options?.occasion);
  if (occasionImg) {
    return occasionImg;
  }

  const genreImg = getGenreBackgroundImage(options?.genre);
  if (genreImg) {
    return genreImg;
  }

  return buildDefaultSongBackgroundUrl();
}
